'use client'

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { RotateCcw, RotateCw } from 'lucide-react'
import { VIDEO_BLANK_POSTER } from '@/lib/utils'
import {
  getVisibleSubtitleWords,
  formatDuration,
  type AIVideoMetadata,
  type SubtitleWord,
} from './scenario-step-attachment'

// ── DesktopVideoPlayer ─────────────────────────────────────────────────────────
// Десктопный плеер в стиле мобильного (MobileMediaLightbox): полупрозрачные кнопки
// −10 / play / +10 по центру, перетаскиваемый ползунок, скорость, субтитры, авто-скрытие.
// С aiMetadata играет только AI-обрезанный фрагмент [start, end] исходного файла.

const SPEEDS = [0.5, 1, 1.5, 2]
const HIDE_DELAY_MS = 3000
const SKIP_SEC = 10
// Смещение мыши (px) после клика-скрытия, при котором контролы снова показываются
const REVEAL_MOVE_THRESHOLD = 16

type Box = { left: number; top: number; width: number; height: number }

function SkipIcon({ forward, size }: { forward: boolean; size: number }) {
  const Icon = forward ? RotateCw : RotateCcw
  return (
    <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <Icon size={size * 0.86} color="white" strokeWidth={2} />
      <span
        className="absolute left-1/2 font-bold text-white leading-none select-none"
        style={{ top: '54%', transform: 'translate(-50%, -50%)', fontSize: Math.round(size * 0.25) }}
      >
        {SKIP_SEC}
      </span>
    </span>
  )
}

export function DesktopVideoPlayer({
  src,
  aiMetadata,
  subtitlesWords,
  className,
  autoPlay = true,
  videoRef: externalVideoRef,
}: {
  src: string
  aiMetadata?: AIVideoMetadata | null
  subtitlesWords?: SubtitleWord[] | null
  className?: string
  autoPlay?: boolean
  /** Доступ к <video> снаружи: перемотка и чтение текущего времени (экран исследования времени) */
  videoRef?: React.RefObject<HTMLVideoElement | null>
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const setVideoEl = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el
      if (externalVideoRef) externalVideoRef.current = el
    },
    [externalVideoRef],
  )
  const barRef = useRef<HTMLDivElement>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const draggingRef = useRef(false)
  const hiddenAtRef = useRef<{ x: number; y: number } | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [absoluteTime, setAbsoluteTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showControls, setShowControls] = useState(true)
  const [showCaptions, setShowCaptions] = useState(true)
  const [speed, setSpeed] = useState(1)
  const [dragging, setDragging] = useState(false)
  const [box, setBox] = useState<Box | null>(null)

  const winStart = aiMetadata
    ? aiMetadata.overwrittenStart != null
      ? aiMetadata.overwrittenStart
      : Math.max(0, aiMetadata.start - 0.5)
    : 0
  const winEnd: number | null = aiMetadata
    ? aiMetadata.overwrittenEnd != null
      ? aiMetadata.overwrittenEnd
      : aiMetadata.nextStartTime > 0
        ? aiMetadata.nextStartTime - 0.5
        : aiMetadata.end
    : null

  const hasCaptions = !!subtitlesWords && subtitlesWords.length > 0
  const visibleWords = useMemo(
    () => (showCaptions && hasCaptions ? getVisibleSubtitleWords(subtitlesWords!, absoluteTime) : []),
    [showCaptions, hasCaptions, subtitlesWords, absoluteTime],
  )

  // ── Авто-скрытие контролов ──────────────────────────────────────────────────
  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null
      const v = videoRef.current
      if (v && !v.paused && !draggingRef.current) setShowControls(false)
    }, HIDE_DELAY_MS)
  }, [clearHideTimer])

  const revealControls = useCallback(() => {
    hiddenAtRef.current = null
    setShowControls(true)
    scheduleHide()
  }, [scheduleHide])

  useEffect(() => clearHideTimer, [clearHideTimer])

  // ── Геометрия: оверлей ровно поверх видимой картинки (object-contain) ─────────
  const updateBox = useCallback(() => {
    const v = videoRef.current
    const c = containerRef.current
    if (!v || !c) return
    const vw = v.videoWidth
    const vh = v.videoHeight
    const vr = v.getBoundingClientRect()
    if (!vw || !vh || !vr.width || !vr.height) return
    const cr = c.getBoundingClientRect()
    const scale = Math.min(vr.width / vw, vr.height / vh)
    const width = vw * scale
    const height = vh * scale
    setBox({
      left: vr.left - cr.left + (vr.width - width) / 2,
      top: vr.top - cr.top + (vr.height - height) / 2,
      width,
      height,
    })
  }, [])

  useEffect(() => {
    const v = videoRef.current
    const c = containerRef.current
    if (!v || !c) return
    const ro = new ResizeObserver(() => updateBox())
    ro.observe(v)
    ro.observe(c)
    return () => ro.disconnect()
  }, [updateBox])

  // ── Сброс состояния при смене ролика (лайтбокс переиспользует компонент) ──────
  useEffect(() => {
    clearHideTimer()
    draggingRef.current = false
    hiddenAtRef.current = null
    setIsPlaying(false)
    setIsLoading(true)
    setCurrentTime(0)
    setAbsoluteTime(winStart)
    setDuration(0)
    setShowControls(true)
    setSpeed(1)
    setDragging(false)
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── События <video> ─────────────────────────────────────────────────────────
  useEffect(() => {
    const v = videoRef.current
    if (!v) return

    const applyDuration = () => {
      if (winEnd != null) setDuration(Math.max(0, winEnd - winStart))
      else if (isFinite(v.duration)) setDuration(v.duration)
    }
    const onMeta = () => {
      if (winEnd != null) v.currentTime = winStart
      // Без автозапуска — первый кадр вместо пустого постера: перемотка снимает постер
      else if (!autoPlay && v.currentTime === 0) v.currentTime = 0
      applyDuration()
      setIsLoading(false)
      updateBox()
      if (autoPlay) v.play().catch(() => {})
    }
    const onTime = () => {
      const abs = v.currentTime
      if (winEnd != null && abs >= winEnd) {
        v.pause()
        v.currentTime = winStart
        setCurrentTime(0)
        setAbsoluteTime(winStart)
        return
      }
      setAbsoluteTime(abs)
      if (!draggingRef.current) setCurrentTime(Math.max(0, abs - winStart))
    }
    const onPlay = () => {
      setIsPlaying(true)
      setIsLoading(false)
      scheduleHide()
    }
    const onPause = () => {
      setIsPlaying(false)
      clearHideTimer()
      hiddenAtRef.current = null
      setShowControls(true)
    }
    const onEnded = () => {
      v.currentTime = winStart
      setCurrentTime(0)
      setAbsoluteTime(winStart)
    }
    const onWaiting = () => setIsLoading(true)
    const onReady = () => setIsLoading(false)

    v.addEventListener('loadedmetadata', onMeta)
    v.addEventListener('durationchange', applyDuration)
    v.addEventListener('timeupdate', onTime)
    v.addEventListener('play', onPlay)
    v.addEventListener('pause', onPause)
    v.addEventListener('ended', onEnded)
    v.addEventListener('waiting', onWaiting)
    v.addEventListener('canplay', onReady)
    v.addEventListener('playing', onReady)
    v.addEventListener('error', onReady)

    if (v.readyState >= 1) onMeta()
    if (v.readyState >= 3) setIsLoading(false)

    return () => {
      v.removeEventListener('loadedmetadata', onMeta)
      v.removeEventListener('durationchange', applyDuration)
      v.removeEventListener('timeupdate', onTime)
      v.removeEventListener('play', onPlay)
      v.removeEventListener('pause', onPause)
      v.removeEventListener('ended', onEnded)
      v.removeEventListener('waiting', onWaiting)
      v.removeEventListener('canplay', onReady)
      v.removeEventListener('playing', onReady)
      v.removeEventListener('error', onReady)
    }
  }, [src, winStart, winEnd, autoPlay, updateBox, scheduleHide, clearHideTimer])

  // ── Действия ────────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      if (winEnd != null && (v.currentTime >= winEnd || v.currentTime < winStart)) v.currentTime = winStart
      v.play().catch(() => {})
    } else {
      v.pause()
    }
  }, [winStart, winEnd])

  const skip = useCallback((sec: number) => {
    const v = videoRef.current
    if (!v) return
    const hi = winEnd ?? (isFinite(v.duration) ? v.duration : Infinity)
    const next = Math.max(winStart, Math.min(v.currentTime + sec, hi))
    v.currentTime = next
    setCurrentTime(Math.max(0, next - winStart))
    revealControls()
  }, [winStart, winEnd, revealControls])

  const cycleSpeed = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]
    v.playbackRate = next
    setSpeed(next)
    revealControls()
  }, [speed, revealControls])

  const seekToClientX = useCallback((clientX: number) => {
    const v = videoRef.current
    const bar = barRef.current
    if (!v || !bar || !(duration > 0)) return
    const rect = bar.getBoundingClientRect()
    const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const rel = pos * duration
    v.currentTime = winStart + rel
    setCurrentTime(rel)
  }, [duration, winStart])

  // ── Ползунок: клик в любую точку + перетаскивание (pointer capture) ──────────
  const onBarPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    draggingRef.current = true
    setDragging(true)
    clearHideTimer()
    setShowControls(true)
    seekToClientX(e.clientX)
  }
  const onBarPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) seekToClientX(e.clientX)
  }
  const onBarPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    draggingRef.current = false
    setDragging(false)
    if (videoRef.current && !videoRef.current.paused) scheduleHide()
  }

  // ── Область видео: клик показывает/прячет контролы, мышь — показывает ────────
  const onAreaClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (showControls) {
      clearHideTimer()
      setShowControls(false)
      hiddenAtRef.current = { x: e.clientX, y: e.clientY }
    } else {
      revealControls()
    }
  }
  const onAreaMouseMove = (e: React.MouseEvent) => {
    const hiddenAt = hiddenAtRef.current
    if (hiddenAt && Math.hypot(e.clientX - hiddenAt.x, e.clientY - hiddenAt.y) < REVEAL_MOVE_THRESHOLD) return
    if (!showControls || hideTimerRef.current || hiddenAt) revealControls()
  }
  const onAreaMouseLeave = () => {
    if (draggingRef.current) return
    const v = videoRef.current
    if (v && !v.paused) {
      clearHideTimer()
      setShowControls(false)
    }
  }

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0
  const compact = !!box && (box.width < 360 || box.height < 280)
  const skipSize = compact ? 44 : 56
  const playSize = compact ? 56 : 72
  const controlsVisible = showControls || dragging

  const roundBtn =
    'flex items-center justify-center rounded-full text-white bg-black/50 backdrop-blur-md ' +
    'hover:bg-black/65 active:scale-95 transition-[background-color,transform] cursor-pointer'
  const pillBtn =
    'shrink-0 inline-flex items-center justify-center h-6 min-w-10 px-2 rounded-md text-xs font-bold leading-none ' +
    'bg-white/15 border-[1.5px] border-white/40 hover:bg-white/25 transition-colors cursor-pointer'

  return (
    <div ref={containerRef} className="relative w-full h-full flex items-center justify-center">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={setVideoEl}
        src={src}
        className={className ?? 'max-w-full max-h-full object-contain'}
        style={{ pointerEvents: box ? 'none' : undefined }}
        preload="metadata"
        playsInline
        poster={VIDEO_BLANK_POSTER}
        onClick={box ? undefined : togglePlay}
      />

      {box && (
        <div
          className="absolute overflow-hidden pointer-events-auto select-none"
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            cursor: controlsVisible || !isPlaying ? 'default' : 'none',
          }}
          onClick={onAreaClick}
          onMouseMove={onAreaMouseMove}
          onMouseLeave={onAreaMouseLeave}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="rounded-full animate-spin"
                style={{
                  width: playSize - 20,
                  height: playSize - 20,
                  border: '3.5px solid rgba(255,255,255,0.25)',
                  borderTopColor: 'rgba(255,255,255,0.92)',
                }}
              />
            </div>
          )}

          {!isLoading && (
            <div
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none transition-opacity duration-300"
              style={{ gap: compact ? 24 : 40, opacity: controlsVisible ? 1 : 0 }}
            >
              <button
                type="button"
                className={roundBtn}
                style={{ width: skipSize, height: skipSize, pointerEvents: controlsVisible ? 'auto' : 'none' }}
                onClick={(e) => { e.stopPropagation(); skip(-SKIP_SEC) }}
                aria-label={`−${SKIP_SEC} секунд`}
              >
                <SkipIcon forward={false} size={skipSize * 0.72} />
              </button>
              <button
                type="button"
                className={roundBtn}
                style={{ width: playSize, height: playSize, pointerEvents: controlsVisible ? 'auto' : 'none' }}
                onClick={(e) => { e.stopPropagation(); togglePlay(); revealControls() }}
                aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
              >
                {isPlaying ? (
                  <svg width={playSize * 0.44} height={playSize * 0.44} fill="white" viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16" rx="1.5" />
                    <rect x="14" y="4" width="4" height="16" rx="1.5" />
                  </svg>
                ) : (
                  <svg width={playSize * 0.44} height={playSize * 0.44} fill="white" viewBox="0 0 24 24" style={{ marginLeft: 3 }}>
                    <path d="M8 5v14l11-7L8 5z" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                className={roundBtn}
                style={{ width: skipSize, height: skipSize, pointerEvents: controlsVisible ? 'auto' : 'none' }}
                onClick={(e) => { e.stopPropagation(); skip(SKIP_SEC) }}
                aria-label={`+${SKIP_SEC} секунд`}
              >
                <SkipIcon forward size={skipSize * 0.72} />
              </button>
            </div>
          )}

          {/* Субтитры видны и при скрытых контролах — тогда опускаются к нижнему краю */}
          {visibleWords.length > 0 && (
            <div
              className="absolute inset-x-0 flex justify-center px-4 pointer-events-none"
              style={{ bottom: controlsVisible ? 64 : 16, transition: 'bottom 0.2s ease-out' }}
            >
              <p
                className="m-0 text-white text-center"
                style={{
                  background: 'rgba(0,0,0,0.82)',
                  padding: '5px 12px',
                  borderRadius: 5,
                  fontSize: 14,
                  lineHeight: 1.45,
                  maxWidth: '90%',
                }}
              >
                {visibleWords.map((w, i) => (
                  <span key={`${w.startTime}-${i}`}>
                    {w.word}
                    {i < visibleWords.length - 1 ? ' ' : ''}
                  </span>
                ))}
              </p>
            </div>
          )}

          <div
            className="absolute inset-x-0 bottom-0 transition-opacity duration-300"
            style={{
              opacity: controlsVisible ? 1 : 0,
              pointerEvents: controlsVisible ? 'auto' : 'none',
              background: 'linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.35) 70%, transparent 100%)',
              padding: '40px 16px 10px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <span className="text-white text-xs shrink-0 tabular-nums" style={{ minWidth: 34 }}>
                {formatDuration(currentTime)}
              </span>

              <div
                ref={barRef}
                className="group relative flex-1 flex items-center cursor-pointer touch-none"
                style={{ height: 28 }}
                onPointerDown={onBarPointerDown}
                onPointerMove={onBarPointerMove}
                onPointerUp={onBarPointerUp}
                onPointerCancel={onBarPointerUp}
                role="slider"
                aria-label="Перемотка"
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(currentTime)}
              >
                <div
                  className={`absolute inset-x-0 rounded-full transition-[height] duration-150 ${
                    dragging ? 'h-[5px]' : 'h-[3px] group-hover:h-[5px]'
                  }`}
                  style={{ background: 'rgba(255,255,255,0.28)' }}
                >
                  <div className="relative h-full rounded-full bg-white" style={{ width: `${pct}%` }}>
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 rounded-full bg-white transition-[scale] duration-150 ${
                        dragging ? 'scale-125' : 'group-hover:scale-110'
                      }`}
                      style={{ right: -7, width: 14, height: 14, boxShadow: '0 1px 5px rgba(0,0,0,0.7)' }}
                    />
                  </div>
                </div>
              </div>

              <span className="text-xs shrink-0 tabular-nums" style={{ minWidth: 34, color: 'rgba(255,255,255,0.60)' }}>
                {formatDuration(duration)}
              </span>

              <button
                type="button"
                onClick={cycleSpeed}
                className={`${pillBtn} text-white`}
                aria-label="Скорость воспроизведения"
              >
                {speed}×
              </button>

              {hasCaptions && (
                <button
                  type="button"
                  onClick={() => { setShowCaptions((p) => !p); revealControls() }}
                  className={`${pillBtn} ${showCaptions ? 'text-white' : 'text-white/45'}`}
                  aria-label={showCaptions ? 'Выключить субтитры' : 'Включить субтитры'}
                >
                  CC
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
